import { isEqual, omit } from 'lodash';
import { Loader, saneBoolean, saneOption } from 'logard';
import { reactive } from 'vue';
import { CLEANING_FILTER_STATUSES } from '../dossier/constants/CleaningStatus';
import ReferenceService from '../general/services/ReferenceService';
import CommonFilterType from '../shared/constants/CommonFilterType';
import { ContextLoader } from '../shared/models/AbstractContext';
import FilterService from '../shared/services/FilterService';
import { extractIds } from '../shared/utils';
import { saneEnum, saneNumericId } from '../shared/utils/sanitizers';
import { present } from '../shared/utils/typescript';
import DossierStackClient from './client/DossierStackClient';
import MemoClient from './client/MemoClient';
import TaskClient from './client/TaskClient';
import InvoicingPeriod from './constants/InvoicingPeriod';
import TaskCategory from './constants/TaskCategory';
import TaskGrouping from './constants/TaskGrouping';
import TaskStatus from './constants/TaskStatus';
import { EXTERIOR_CLEANING_PARAM, INTERIOR_CLEANING_PARAM, MEMO_TAGS_PARAM, TASK_GROUPING_PARAM, TASK_STATUS_PARAM, TODAY_CLEANING_PARAM } from './constants/params';
import { DossierStackId } from './models/DossierStack';
import { MemoTagId } from './models/MemoTag';
import TaskFilters from './models/TaskFilters';
import TaskOverviewContext from './models/TaskOverviewContext';
import MemoService from './services/MemoService';
import TaskService from './services/TaskService';

export const taskPageLoader = new ContextLoader<TaskOverviewContext>(async (s, previous) => {
  const filterTypes = present(s.getAttribute<Array<CommonFilterType>>('commonFilters'));
  const filters = new TaskFilters(await FilterService.getFilters(s, filterTypes));
  filters.status = present(s.getQueryParam(TASK_STATUS_PARAM, saneEnum(TaskStatus), previous?.filters.status ?? TaskStatus.OPEN));
  const context = reactive(new TaskOverviewContext(filters)) as TaskOverviewContext;
  TaskService.bindOverviewUpdateHandlers(context);

  await Promise.all([
    TaskClient.loadAll(context.filters.asTaskCriteria()).then(c => context.setTaskContainer(c)),
    TaskService.loadInvoicingSummary(context),
  ]);

  context.freeHooks.push(() => TaskService.clearGlobalUpdates());

  return context;
});

export const taskOverviewLoader = new Loader<TaskOverviewContext>(async (s, previous) => {
  const context = await taskPageLoader.getResult(s);
  context.filters.grouping = present(s.getQueryParam(TASK_GROUPING_PARAM, saneEnum(TaskGrouping), previous?.filters.grouping ?? TaskGrouping.BY_DOSSIER));
  context.category = s.getPathParam('category', saneEnum(TaskCategory)) ?? null;
  return context;
});

export const invoicingOverviewLoader = new Loader(async s => {
  const context = await taskPageLoader.getResult(s);
  context.invoicingPeriod = s.getPathParam('period', saneEnum(InvoicingPeriod)) ?? null;
  return context;
});

export const memoOverviewLoader = new Loader(async s => {
  const context = await taskPageLoader.getResult(s);
  context.filters.memoTags = await MemoService.loadTags();
  context.filters.activeMemoTagIds = s.getQueryParams(MEMO_TAGS_PARAM, saneOption(extractIds(context.filters.memoTags), saneNumericId<MemoTagId>));
  context.memos = await MemoClient.loadAllTaskMemos(context.filters.asMemoCriteria());
  const uniqueOutletIds = [...new Set(context.memos.map(memo => memo.outletId))];
  await Promise.all(uniqueOutletIds.map(outletId => ReferenceService.loadChatGroups(outletId)));
  return context;
});

export const stackLoader = new Loader(async s => {
  const context = await taskPageLoader.getResult(s);
  const stackId = present(s.getPathParam('stackId', saneOption(extractIds(context.stacks), saneNumericId<DossierStackId>)));
  context.currentStack = present(context.stacks.find(ds => ds.id === stackId));
  context.currentStackItems = await DossierStackClient.loadItems(stackId, context.filters.common.activeOutletIds);
  return context;
});

export const assignedDossierOverviewLoader = new Loader(async s => {
  const context = await taskPageLoader.getResult(s);
  const tasks = await TaskClient.loadAssignedDossierTasks(context.filters.asAssignedDossiersCriteria());
  context.setAssignedDossierTasks(tasks);
  return context;
});

export const cleaningOverviewLoader = new ContextLoader<TaskOverviewContext>(async (s, previous) => {
  const parentContext = await taskPageLoader.getResult(s);
  const filters = new TaskFilters(parentContext.filters.common);
  filters.interiorCleaningStatus = s.getQueryParam(INTERIOR_CLEANING_PARAM, saneOption(CLEANING_FILTER_STATUSES)) ?? null;
  filters.exteriorCleaningStatus = s.getQueryParam(EXTERIOR_CLEANING_PARAM, saneOption(CLEANING_FILTER_STATUSES)) ?? null;
  filters.cleaningStatusToday = present(s.getQueryParam(TODAY_CLEANING_PARAM, saneBoolean, false));
  const context = reactive(new TaskOverviewContext(filters)) as TaskOverviewContext;
  TaskService.bindCleaningOverviewUpdateHandlers(context);

  const needsReload = !isEqual(omit(previous?.filters, 'cleaningStatusToday'), omit(filters, 'cleaningStatusToday'));
  if (needsReload) context.cleaningTasks = await TaskClient.loadCleaningTasks(filters.asCleaningCriteria());
  else context.cleaningTasks = previous ? [...previous.cleaningTasks] : [];
  return context;
});
